/**
 * @file Header.jsx
 * @description Encabezado responsivo con navegación y cajón deslizable para móvil.
 */

import React, { useState, useContext } from 'react';
import {
  Box,
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  SwipeableDrawer,
  IconButton,
} from '@mui/material';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import MenuIcon from '@mui/icons-material/Menu';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import { Container } from 'react-bootstrap';
import { Link } from 'react-router-dom';

import Logo from '../assets/images/logo-delirous.png';
import { menuItems } from '../Menu';
import { ThemeContext } from '../App';

const Header = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { theme, toggleTheme } = useContext(ThemeContext);

  /**
   * Alterna el cajón deslizable abierto/cerrado
   * @param {boolean} open - Si el cajón debe estar abierto
   */
  const toggleDrawer = (open) => (event) => {
    if (
      event?.type === 'keydown' &&
      (event.key === 'Tab' || event.key === 'Shift')
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  /**
   * Renderiza la lista del menú móvil dentro del cajón deslizable
   */
  const renderDrawerList = () => (
    <Box
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
      sx={{ width: 250 }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <img
          src={Logo}
          alt="DeliRous Restaurant Logo"
          width={180}
          className="logo"
          loading="lazy"
        />
      </Box>
      <Divider />
      <List>
        {menuItems.map((menu) => (
          <ListItem
            button
            component="a"
            href={menu.path}
            key={menu.label}
            sx={{ py: 1 }}
          >
            <ListItemIcon>
              <KeyboardReturnIcon className="fa-flip-horizontal" />
            </ListItemIcon>
            <ListItemText primary={menu.label} />
          </ListItem>
        ))}
        <ListItem button onClick={toggleTheme} sx={{ py: 1 }}>
          <ListItemIcon sx={{ color: 'var(--accent-gold)' }}>
            {theme === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
          </ListItemIcon>
          <ListItemText primary={theme === 'dark' ? 'Modo Claro' : 'Modo Oscuro'} />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <header id="header">
      <Container>
        {/* Cajón de Navegación Móvil */}
        <SwipeableDrawer
          anchor="left"
          open={drawerOpen}
          onClose={toggleDrawer(false)}
          onOpen={toggleDrawer(true)}
        >
          {renderDrawerList()}
        </SwipeableDrawer>

        {/* Logo */}
        <Link to="/" aria-label="Ir a la página principal">
          <img
            src={Logo}
            alt="DeliRous Restaurant Logo"
            width={200}
            className="logo"
            loading="lazy"
          />
        </Link>

        {/* Botón de Menú Móvil */}
        <IconButton
          onClick={toggleDrawer(true)}
          className="d-block d-md-none menuicon"
          aria-label="Abrir menú"
          sx={{ color: 'var(--accent-gold)' }}
        >
          <MenuIcon fontSize="large" />
        </IconButton>

        {/* Navegación de Escritorio */}
        <nav id="navbar" className="navbar d-none d-md-block" role="navigation">
          <ul>
            {menuItems.map((menu) => (
              <li key={menu.label}>
                <a href={menu.path}>{menu.label}</a>
              </li>
            ))}
            <li>
              <IconButton onClick={toggleTheme} color="inherit" aria-label="Toggle light/dark theme" sx={{ color: 'var(--accent-gold)' }}>
                {theme === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
            </li>
          </ul>
        </nav>
      </Container>
    </header>
  );
};

export default Header;
